import { oc } from "@orpc/contract";
import { z } from "zod";

// ─── Shared schemas ─────────────────────────────────────────────────────────

/** One override row (history-preserving — includes revoked / expired rows). */
export const InventoryPolicyOverrideRowSchema = z.object({
  id: z.string().uuid(),
  inventoryPolicyId: z.string().uuid(),
  minQuantity: z.number().nullable(),
  maxQuantity: z.number().nullable(),
  autoTransferEligible: z.boolean().nullable(),
  autoBuyEligible: z.boolean().nullable(),
  startsAt: z.string(),
  endsAt: z.string().nullable(),
  reason: z.string().nullable(),
  createdByUserId: z.string().uuid(),
  createdByUserName: z.string().nullable(),
  deactivatedAt: z.string().nullable(),
  /** True iff active right now (not deactivated, within [startsAt, endsAt)). */
  isActive: z.boolean(),
  createdAt: z.string(),
});

export type InventoryPolicyOverrideRow = z.infer<
  typeof InventoryPolicyOverrideRowSchema
>;

/** The resolved effective policy (base ⊕ active overlay) for display. */
export const EffectivePolicySchema = z.object({
  policyId: z.string().uuid(),
  warehouseId: z.string().uuid(),
  productId: z.string().uuid(),
  productVariantId: z.string().uuid().nullable(),
  minQuantity: z.number(),
  maxQuantity: z.number(),
  autoTransferEligible: z.boolean(),
  autoBuyEligible: z.boolean(),
  baseMinQuantity: z.number(),
  baseMaxQuantity: z.number(),
  baseAutoTransferEligible: z.boolean(),
  baseAutoBuyEligible: z.boolean(),
  // Forecast-computed levels (read-only; null until the forecast has run).
  safetyStock: z.number().nullable(),
  reorderPoint: z.number().nullable(),
  orderUpToLevel: z.number().nullable(),
  activeOverrideCount: z.number(),
});

export type EffectivePolicy = z.infer<typeof EffectivePolicySchema>;

// ─── Create ─────────────────────────────────────────────────────────────────

export const CreateInventoryPolicyOverrideInputSchema = z
  .object({
    // Target policy by its natural key (matches how the board addresses a line).
    warehouseId: z.string().uuid(),
    productId: z.string().uuid(),
    productVariantId: z.string().uuid(),
    // Overridable fields. Omit / null = "this override doesn't touch it".
    minQuantity: z.number().nonnegative().nullable().optional(),
    maxQuantity: z.number().nonnegative().nullable().optional(),
    autoTransferEligible: z.boolean().nullable().optional(),
    autoBuyEligible: z.boolean().nullable().optional(),
    // Window. startsAt defaults to now; endsAt null = permanent.
    startsAt: z.string().datetime().optional(),
    endsAt: z.string().datetime().nullable().optional(),
    reason: z.string().max(1000).nullable().optional(),
  })
  .refine(
    (v) =>
      v.minQuantity != null ||
      v.maxQuantity != null ||
      v.autoTransferEligible != null ||
      v.autoBuyEligible != null,
    { message: "An override must set at least one field." },
  )
  .refine(
    (v) =>
      v.minQuantity == null ||
      v.maxQuantity == null ||
      v.maxQuantity >= v.minQuantity,
    {
      message: "maxQuantity must be >= minQuantity.",
    },
  )
  .refine(
    (v) => {
      if (!v.endsAt) return true;
      const start = v.startsAt ? new Date(v.startsAt) : new Date();
      return new Date(v.endsAt).getTime() > start.getTime();
    },
    {
      path: ["endsAt"],
      message: "endsAt must be later than startsAt.",
    },
  );

export const CreateInventoryPolicyOverrideResponseSchema = z.object({
  override: InventoryPolicyOverrideRowSchema,
  effective: EffectivePolicySchema,
});

export type CreateInventoryPolicyOverrideInput = z.infer<
  typeof CreateInventoryPolicyOverrideInputSchema
>;
export type CreateInventoryPolicyOverrideResponse = z.infer<
  typeof CreateInventoryPolicyOverrideResponseSchema
>;

// ─── List by policy ──────────────────────────────────────────────────────────

export const ListInventoryPolicyOverridesInputSchema = z.object({
  warehouseId: z.string().uuid(),
  productId: z.string().uuid(),
  productVariantId: z.string().uuid(),
});

export const ListInventoryPolicyOverridesResponseSchema = z.object({
  policyId: z.string().uuid().nullable(),
  effective: EffectivePolicySchema.nullable(),
  overrides: z.array(InventoryPolicyOverrideRowSchema),
});

export type ListInventoryPolicyOverridesInput = z.infer<
  typeof ListInventoryPolicyOverridesInputSchema
>;
export type ListInventoryPolicyOverridesResponse = z.infer<
  typeof ListInventoryPolicyOverridesResponseSchema
>;

// ─── List by product (one row per warehouse×variant policy combo) ────────────

/** One policy combo for a product — effective + base values for the table. */
export const PolicyOverrideComboRowSchema = z.object({
  policyId: z.string().uuid(),
  warehouseId: z.string().uuid(),
  warehouseName: z.string(),
  divisionName: z.string().nullable(),
  productVariantId: z.string().uuid(),
  variantLabel: z.string().nullable(),
  minQuantity: z.number(),
  maxQuantity: z.number(),
  autoTransferEligible: z.boolean(),
  autoBuyEligible: z.boolean(),
  baseMinQuantity: z.number(),
  baseMaxQuantity: z.number(),
  baseAutoTransferEligible: z.boolean(),
  baseAutoBuyEligible: z.boolean(),
  safetyStock: z.number().nullable(),
  reorderPoint: z.number().nullable(),
  orderUpToLevel: z.number().nullable(),
  activeOverrideCount: z.number(),
});

export type PolicyOverrideComboRow = z.infer<
  typeof PolicyOverrideComboRowSchema
>;

export const ListPolicyOverrideCombosInputSchema = z.object({
  productId: z.string().uuid(),
});

export const ListPolicyOverrideCombosResponseSchema = z.object({
  rows: z.array(PolicyOverrideComboRowSchema),
});

export type ListPolicyOverrideCombosInput = z.infer<
  typeof ListPolicyOverrideCombosInputSchema
>;
export type ListPolicyOverrideCombosResponse = z.infer<
  typeof ListPolicyOverrideCombosResponseSchema
>;

// ─── Revoke ──────────────────────────────────────────────────────────────────

export const RevokeInventoryPolicyOverrideInputSchema = z.object({
  overrideId: z.string().uuid(),
});

export const RevokeInventoryPolicyOverrideResponseSchema = z.object({
  override: InventoryPolicyOverrideRowSchema,
});

export type RevokeInventoryPolicyOverrideInput = z.infer<
  typeof RevokeInventoryPolicyOverrideInputSchema
>;
export type RevokeInventoryPolicyOverrideResponse = z.infer<
  typeof RevokeInventoryPolicyOverrideResponseSchema
>;

// ─── Contract ───────────────────────────────────────────────────────────────

export const inventoryPolicyOverridesContracts = {
  create: oc
    .input(CreateInventoryPolicyOverrideInputSchema)
    .output(CreateInventoryPolicyOverrideResponseSchema)
    .route({
      method: "POST",
      summary:
        "Create a time-gated override pinning inventory-policy fields (min/max/auto-eligibility) over a window.",
    }),
  listByPolicy: oc
    .input(ListInventoryPolicyOverridesInputSchema)
    .output(ListInventoryPolicyOverridesResponseSchema)
    .route({
      method: "GET",
      summary:
        "List all overrides (active + historical) for a policy, plus the resolved effective values.",
    }),
  listByProduct: oc
    .input(ListPolicyOverrideCombosInputSchema)
    .output(ListPolicyOverrideCombosResponseSchema)
    .route({
      method: "GET",
      summary:
        "List every inventory-policy combo (warehouse×variant) for a product with effective + base values, for the product-detail overrides table.",
    }),
  revoke: oc
    .input(RevokeInventoryPolicyOverrideInputSchema)
    .output(RevokeInventoryPolicyOverrideResponseSchema)
    .route({
      method: "POST",
      summary:
        "Revoke an override immediately (sets deactivated_at); any prior override resumes.",
    }),
} as const;
